import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { faThumbsUp, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { insightBannerColors } from "@utilifeed/components/propai";
import { NormalizedInsight } from "@core/apis/PropAI/types/insights.type";

import { INSIGHT_CONFIG } from "./insightConfig";
import { InsightRow } from "./InsightRow";
import { InsightTableCard } from "./InsightTableCard";

type InsightsSectionProps = Readonly<{
  goodInsights: NormalizedInsight[];
  badInsights: NormalizedInsight[];
}>;

function getInsightKey(insight: NormalizedInsight, index: number): string {
  const config = INSIGHT_CONFIG[insight.type];
  if (config?.category === "sensor") {
    return `${insight.type}-${insight.meta_data.sensor_id}`;
  }
  return `${insight.type}-${index}`;
}

export function InsightsSection({ goodInsights, badInsights }: InsightsSectionProps) {
  const { t } = useTranslation(["propai_insights"]);

  const getInsightColumns = (actionLabel: string) => [
    { label: t("we_have_noticed_that"), width: "20%" },
    { label: t("description"), width: "40%" },
    { label: actionLabel, width: "40%" },
  ];

  return (
    <>
      {/* Bad Insights */}
      {badInsights.length > 0 && (
        <Grid item xs={12}>
          <InsightTableCard
            title={t("room_for_improvements")}
            icon={faTriangleExclamation}
            testId="bad-insights-table"
            bannerColor={insightBannerColors.badInsights}
            columns={getInsightColumns(t("recommended_action"))}
          >
            {badInsights.map((insight, index) => (
              <InsightRow key={getInsightKey(insight, index)} insight={insight} />
            ))}
          </InsightTableCard>
        </Grid>
      )}

      {/* Good Insights */}
      {goodInsights.length > 0 && (
        <Grid item xs={12}>
          <InsightTableCard
            title={t("good_job")}
            icon={faThumbsUp}
            testId="good-insights-table"
            bannerColor={insightBannerColors.goodInsights}
            columns={getInsightColumns(t("upside"))}
          >
            {goodInsights.map((insight, index) => (
              <InsightRow key={getInsightKey(insight, index)} insight={insight} />
            ))}
          </InsightTableCard>
        </Grid>
      )}
    </>
  );
}
